/**
 * Converts an object using mapping dictionaries.
 *
 * @template T - The type of the source data object.
 * @template D - The type of the mapping dictionary.
 * @template I - The type of the reverse mapping dictionary.
 * @template S - The type of the resulting data object.
 *
 * @param {T} params - The data object to convert.
 * @param {D} dictionary - The mapping dictionary.
 * @param {I} reverseDictionary - The reverse mapping dictionary.
 * @returns {S} Returns the converted data object.
 */
export function convertObjectByDictionaries<
  T extends Record<keyof D | keyof I | string, unknown> | Gtag.ControlParams,
  D extends Record<string, keyof I | undefined>,
  I extends Record<string, keyof D | undefined>,
  S extends Record<keyof I | string, unknown>
>(params: T, dictionary: D, reverseDictionary: I): S {
  const result: S = {} as S;
  for (const key in params) {
    if (!Object.prototype.hasOwnProperty.call(params, key)) {
      continue;
    }
    const value = params[key] as unknown as S[keyof I];
    if (value === null || value === undefined) {
      continue;
    }
    const current: keyof I | undefined = dictionary[key];
    if (current) {
      result[current] = value;
    } else if (reverseDictionary[key] !== undefined) {
      result[key as keyof I] = value;
    }
  }
  return result;
}

/**
 * Safely parses a JSON string.
 *
 * @param text - The JSON string to parse.
 * @returns The parsed object if valid, otherwise `null`.
 */
export const parseJson = (text: string) => {
  try {
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
};
